import axios from 'axios';
import {
  CaddiePrimaryFocus,
  CaddieRecommendationDepth,
  CaddieTalkativeness,
  CaddieVoicePreference,
  CaddieVoiceStyle,
  CourseSearchItem,
  Handedness,
  MeasurementSystem,
  ProfileResponse,
} from '../types';
import { attachAuthInterceptor } from './authToken';

const BASE_URL = (process.env.REACT_APP_CLUBHOUSE_API_URL || 'http://localhost:5000').replace(/\/+$/, '');

const profilePath = (userId: string): string => `/profiles/${encodeURIComponent(userId)}`;

const apiClient = axios.create({
  baseURL: `${BASE_URL}/api`,
});
attachAuthInterceptor(apiClient);

export interface SaveProfileInput {
  name: string;
  /** Golfer contact email entered when Entra does not provide an external email claim. */
  email?: string;
  state: string;
  /** Which hand the golfer swings with. Omit to keep the current value. */
  handedness?: Handedness;
  caddieVoicePreference: CaddieVoicePreference;
  /** How talkative the caddie should be. */
  caddieTalkativeness: CaddieTalkativeness;
  /** Tone/energy of the caddie's delivery. */
  caddieVoiceStyle: CaddieVoiceStyle;
  /** How much planning/tradeoff detail the caddie surfaces. */
  caddieRecommendationDepth: CaddieRecommendationDepth;
  /** Which dimension the caddie emphasizes. */
  caddiePrimaryFocus: CaddiePrimaryFocus;
  measurementSystem: MeasurementSystem;
  /** Subscription tier / plan id to set. Omit to keep the current plan. */
  productOfferingId?: string;
  /** Access code required when selecting the gated `Trial` plan. */
  trialCode?: string;
  /**
   * The promo campaign the golfer arrived through (e.g. `end-of-season`). When it is open and the
   * golfer is new, the API starts that campaign's free trial instead of the requested plan.
   */
  promoCampaign?: string;
  /** New avatar image to upload. Omit to keep the current one. */
  avatar?: Blob | null;
  /** Clear the stored avatar (ignored when `avatar` is provided). */
  removeAvatar?: boolean;
}

export const profileApi = {
  /** Builds the avatar URL against the client's configured API host (host-independent). */
  avatarUrl: (userId: string, version: string): string =>
    `${BASE_URL}/api${profilePath(userId)}/avatar?v=${encodeURIComponent(version)}`,

  /** Fetches a versioned avatar URL through Axios so authenticated responses remain browser-cacheable. */
  getAvatar: async (avatarUrl: string): Promise<Blob | null> => {
    try {
      const { data } = await apiClient.get<Blob>(avatarUrl, { responseType: 'blob' });
      return data;
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 404) return null;
      throw err;
    }
  },

  /** Fetches the saved profile, or `null` when none has been saved yet. */
  get: async (userId: string): Promise<ProfileResponse | null> => {
    try {
      const { data } = await apiClient.get<ProfileResponse>(profilePath(userId));
      return data;
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 404) return null;
      throw err;
    }
  },

  /** Creates or updates the profile in the storage account. */
  save: async (userId: string, input: SaveProfileInput): Promise<ProfileResponse> => {
    const formData = new FormData();
    formData.append('name', input.name);
    if (input.email) formData.append('email', input.email);
    formData.append('state', input.state);
    if (input.handedness) formData.append('handedness', input.handedness);
    formData.append('caddieVoicePreference', input.caddieVoicePreference);
    formData.append('caddieTalkativeness', input.caddieTalkativeness);
    formData.append('caddieVoiceStyle', input.caddieVoiceStyle);
    formData.append('caddieRecommendationDepth', input.caddieRecommendationDepth);
    formData.append('caddiePrimaryFocus', input.caddiePrimaryFocus);
    formData.append('measurementSystem', input.measurementSystem);
    if (input.productOfferingId) formData.append('productOfferingId', input.productOfferingId);
    if (input.trialCode) formData.append('trialCode', input.trialCode);
    if (input.promoCampaign) formData.append('promoCampaign', input.promoCampaign);
    if (input.avatar) formData.append('avatar', input.avatar, 'avatar.jpg');
    if (input.removeAvatar) formData.append('removeAvatar', 'true');

    const { data } = await apiClient.put<ProfileResponse>(profilePath(userId), formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  /** Persists the user's saved course list and current selection to the profile JSON. */
  saveCourses: async (
    userId: string,
    courses: CourseSearchItem[],
    selectedCourseId: string | null,
  ): Promise<ProfileResponse> => {
    const { data } = await apiClient.put<ProfileResponse>(`${profilePath(userId)}/courses`, {
      courses,
      selectedCourseId,
    });
    return data;
  },

  /** Persists just the active round (selection + current hole) so the watch/phone/web stay on the same hole. */
  saveRound: async (
    userId: string,
    selectedCourseId: string | null,
    currentHole: number,
  ): Promise<ProfileResponse> => {
    const { data } = await apiClient.put<ProfileResponse>(`${profilePath(userId)}/round`, {
      selectedCourseId,
      currentHole,
    });
    return data;
  },

  /** Cancels recurring billing when needed and irreversibly soft-deletes the golfer's account. */
  deleteAccount: async (userId: string): Promise<void> => {
    await apiClient.delete(profilePath(userId));
  },
};